import { Avatar } from "@msuite/ui/avatar";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { createdAtFormatter } from "@msuite/utils/date";
import type { RoleMember } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { RoleMemberRowActions } from "./role-member-row-actions";

const columnHelper = createColumnHelper<RoleMember>();

/**
 * The members table's columns: the person, when they received the role, who handed it out, and
 * the row actions. The name is an accessor rather than a display column so the table's built-in
 * search matches it; `addedBy` reads as an em dash for standing and backfilled memberships,
 * which carry no actor.
 */
export function createMemberColumns(roleId: string) {
	return [
		columnHelper.accessor((member) => getFullName(member), {
			id: "name",
			header: localized("general.name"),
			cell: (cell) => (
				<div
					className="items-center gap-2 h-stack"
					data-cy={`role-member-${cell.row.original.id}`}
				>
					<Avatar
						size="xs"
						variant={cell.row.original.image ? "image" : "initials"}
						imageUrl={cell.row.original.image}
						initials={cell.row.original.initials}
					/>
					<p className="truncate">{cell.getValue()}</p>
				</div>
			),
		}),
		columnHelper.accessor(createdAtFormatter, {
			id: "createdAt",
			header: localized("roles.added_at"),
			cell: (cell) => cell.getValue(),
		}),
		columnHelper.accessor((member) => (member.addedBy ? getFullName(member.addedBy) : "—"), {
			id: "addedBy",
			header: localized("roles.added_by"),
			cell: (cell) => (
				<p data-cy={`role-member-added-by-${cell.row.original.id}`}>{cell.getValue()}</p>
			),
		}),
		createRowActions<RoleMember>((props) => (
			<RoleMemberRowActions
				{...props}
				roleId={roleId}
			/>
		)),
	];
}
